import isUndefined from 'lodash-es/isUndefined';
import { isPhrasingContent, isPresentationalPhrasingContent } from '../dom/content';
import { simplifyElement } from '../dom/element';
import { isHTMLOrSVGElement } from '../dom/node';
import { paintNodesInRange } from '../dom/paint';
import { getComputedStyle } from './computedStyle';

export const FONT_WEIGHT_ALIASES: { [key: string]: number } = {
  normal: 400,
  bold: 700,
};

export function nodeIsBold(node: Node, container: Node): boolean {
  const style = getComputedStyle(node, container);

  return !!style && computedStyleIsBold(style);
}

export function computedStyleIsBold(computedStyle: CSSStyleDeclaration): boolean {
  return styleIsBold(computedStyle);
}

export function styleIsBold(style: CSSStyleDeclaration): boolean {
  return fontWeightIsBold(style.fontWeight);
}

export function fontWeightIsBold(fontWeight: string | null): boolean {
  if (!fontWeight) {
    return false;
  }

  // TODO: Calculate the actual font weight from this relative weight.
  if (fontWeight === 'bolder') {
    return true;
  }

  return getNumericFontWeight(fontWeight) > FONT_WEIGHT_ALIASES.normal;
}

export function getNumericFontWeight(fontWeight: string): number {
  if (fontWeight in FONT_WEIGHT_ALIASES) {
    return FONT_WEIGHT_ALIASES[fontWeight];
  }

  const parsed = parseInt(fontWeight, 10);
  return isUndefined(parsed) ? FONT_WEIGHT_ALIASES.normal : parsed;
}

export function clearBoldInElement(element: HTMLElement | SVGElement): void {
  // eslint-disable-next-line no-param-reassign
  element.style.fontWeight = '';
}

export function clearBoldInRange(container: Node, range: Range): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node => nodeIsBold(node, container),
    shouldSplitElement: element => isPresentationalPhrasingContent(element),
    paintElement: element => {
      let shouldSimplify = ['B', 'STRONG'].includes(element.tagName);

      if (isHTMLOrSVGElement(element) && fontWeightIsBold(element.style.fontWeight)) {
        shouldSimplify = shouldSimplify || element.tagName === 'SPAN';

        clearBoldInElement(element);
      }

      if (shouldSimplify) {
        return simplifyElement(element);
      }

      return element;
    },
  });
}

export function setBoldInElement(element: HTMLElement | SVGElement): void {
  // eslint-disable-next-line no-param-reassign
  element.style.fontWeight = 'bold';
}

export function setBoldInRange(container: Node, range: Range): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node => isPhrasingContent(node) && !nodeIsBold(node, container),
    createPaintedElement: document => document.createElement('b'),
    paintElement: element => {
      if (isHTMLOrSVGElement(element)) {
        setBoldInElement(element);
      }

      return element;
    },
    forcePaintElement: element => {
      if (isHTMLOrSVGElement(element)) {
        // eslint-disable-next-line no-param-reassign
        element.style.fontWeight = 'bold !important';
      }

      return element;
    },
  });
}
